/**
 * useHoldToRecord (Task 24, TDD).
 *
 * Pointer-down/up gesture wrapping MediaRecorder. States:
 *   idle → preparing (mic permission) → recording → stopping → submitted | error
 *
 * Guards (per plan + Q):
 *   - Min duration 500ms (below = onError("too_short"), no blob handed to onSubmit)
 *   - Max duration 5 minutes (auto-stop)
 *   - Pointer cancel (drag-off / pointerleave) → state returns to idle, no submission
 *   - onSubmit called exactly once per release on the happy path
 */

import { useCallback, useEffect, useRef, useState } from "react";

export type HoldToRecordState =
  | "idle"
  | "preparing"
  | "recording"
  | "stopping"
  | "submitted"
  | "error";

export type HoldToRecordError =
  | "too_short"
  | "permission_denied"
  | "no_media_recorder"
  | "recording_failed";

export type UseHoldToRecordOptions = {
  onSubmit: (blob: Blob, durationMs: number) => void;
  onError?: (reason: HoldToRecordError) => void;
  minDurationMs?: number;
  maxDurationMs?: number;
  mimeType?: string;
};

export type UseHoldToRecordReturn = {
  state: HoldToRecordState;
  durationMs: number;
  start: () => Promise<void>;
  stop: () => void;
  cancel: () => void;
  reset: () => void;
};

const DEFAULT_MIN_MS = 500;
const DEFAULT_MAX_MS = 5 * 60 * 1000;

export function useHoldToRecord(options: UseHoldToRecordOptions): UseHoldToRecordReturn {
  const minMs = options.minDurationMs ?? DEFAULT_MIN_MS;
  const maxMs = options.maxDurationMs ?? DEFAULT_MAX_MS;

  const [state, setState] = useState<HoldToRecordState>("idle");
  const [durationMs, setDurationMs] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);
  const maxTimerRef = useRef<number | null>(null);
  const submittedOnceRef = useRef(false);
  // Generation counter so that cancel() invalidates any in-flight start() that's
  // still awaiting getUserMedia. Without this, a user who leaves/releases while
  // the browser's permission popup is up could end up recording anyway when the
  // promise eventually resolves.
  const startGenRef = useRef(0);

  const cleanup = useCallback(() => {
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (maxTimerRef.current !== null) {
      window.clearTimeout(maxTimerRef.current);
      maxTimerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    recorderRef.current = null;
    chunksRef.current = [];
    startedAtRef.current = null;
    submittedOnceRef.current = false;
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  const start = useCallback(async () => {
    if (state !== "idle") return;
    setState("preparing");
    const myGen = ++startGenRef.current;

    if (typeof MediaRecorder === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setState("error");
      options.onError?.("no_media_recorder");
      return;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      // Don't overwrite state if we were already cancelled.
      if (myGen === startGenRef.current) {
        setState("error");
        options.onError?.("permission_denied");
      }
      return;
    }

    // If cancel() ran while we were awaiting the permission prompt, abort.
    if (myGen !== startGenRef.current) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }

    const mimeType = options.mimeType ?? (MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : "audio/webm");

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, { mimeType });
    } catch {
      stream.getTracks().forEach((t) => t.stop());
      setState("error");
      options.onError?.("recording_failed");
      return;
    }

    recorderRef.current = recorder;
    streamRef.current = stream;
    chunksRef.current = [];
    startedAtRef.current = Date.now();
    submittedOnceRef.current = false;

    recorder.addEventListener("dataavailable", (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    });

    recorder.addEventListener("stop", () => {
      const elapsed = startedAtRef.current ? Date.now() - startedAtRef.current : 0;
      const blob = new Blob(chunksRef.current, { type: mimeType });

      if (elapsed < minMs) {
        cleanup();
        setState("error");
        options.onError?.("too_short");
        return;
      }

      if (!submittedOnceRef.current) {
        submittedOnceRef.current = true;
        options.onSubmit(blob, elapsed);
        setState("submitted");
      }
      cleanup();
    });

    recorder.start(250); // dataavailable every 250ms
    setState("recording");

    tickRef.current = window.setInterval(() => {
      if (startedAtRef.current) {
        setDurationMs(Date.now() - startedAtRef.current);
      }
    }, 100);

    maxTimerRef.current = window.setTimeout(() => {
      if (recorderRef.current?.state === "recording") {
        recorderRef.current.stop();
      }
    }, maxMs);
  }, [state, options, minMs, maxMs, cleanup]);

  const stop = useCallback(() => {
    if (recorderRef.current?.state === "recording") {
      setState("stopping");
      recorderRef.current.stop();
    }
  }, []);

  const cancel = useCallback(() => {
    // Invalidate any in-flight start() so a late-resolving getUserMedia doesn't
    // sneak through and create a recorder we never asked for.
    startGenRef.current++;
    if (recorderRef.current?.state === "recording") {
      // Stop without firing submission: clear chunks so the stop handler sees zero data.
      chunksRef.current = [];
      submittedOnceRef.current = true; // suppress submission
      recorderRef.current.stop();
    }
    cleanup();
    setState("idle");
    setDurationMs(0);
  }, [cleanup]);

  const reset = useCallback(() => {
    cleanup();
    setState("idle");
    setDurationMs(0);
  }, [cleanup]);

  return { state, durationMs, start, stop, cancel, reset };
}
